import { rem } from 'polished'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useNavigateTo from 'hooks/useNavigateTo'
import { pluralizeToString } from 'lib/string/pluralize'
import { isMobileSel } from 'selectors/configSelectors'
import { CopyTemplateError } from 'tripPlanner/api/types'
import CuratedItemSelection from 'tripPlanner/components/CuratedItemSelection/CuratedItemSelection'
import { CuratedSectionSelection } from 'tripPlanner/components/CuratedItemSelection/CuratedSectionSelection'
import { useCopyTripTemplate } from 'tripPlanner/hooks/api'
import {
  closeMobileModal,
  triggerMobileError,
} from 'tripPlanner/hooks/mobileApi'
import useSaveButtonOnboardingTooltipRules from 'tripPlanner/hooks/onboarding/useSaveButtonOnboardingTooltipRules'
import useCuratedTripSummaryData from 'tripPlanner/hooks/useCuratedTripSummaryData'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import TripPlannerFilters from 'tripPlanner/pages/trip/TripPlannerFilters'
import { CuratedTripSection, FullTrip } from 'tripPlanner/types/common'

const ButtonGroup = styled(Group)`
  > * {
    flex: 1;
  }
`

const Filters = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${rem(8)};
  padding: ${rem(8)} 0 0;
`

interface Props {
  template: FullTrip
  name: string
}

export default function SelectItemModal({ template, name }: Props) {
  const {
    summarySections,
    itemCount,
    filteredTripSummary,
    availableSubFilters,
    filters,
  } = useCuratedTripSummaryData({ trip: template })
  const isMobileApp = useAppSelector(isMobileSel)

  const { currentRegionCode } = useContext(GeoContext)
  const { setUserHasSaved } = useSaveButtonOnboardingTooltipRules()

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const navigateTo = useNavigateTo()
  const {
    mutate: copyTemplate,
    isLoading,
    isError,
  } = useCopyTripTemplate({
    onSuccess: (trip) => {
      setUserHasSaved()
      closeModal()
      if (isMobileApp) {
        closeMobileModal()
      } else {
        navigateTo(`/trip-planner/trip/${trip.id}/summary`)
      }
    },
    onError: (e: CopyTemplateError) => {
      const message =
        e.status === 404 ?
          'Looks like the template you are trying to copy is gone.' :
          e.message

      setErrorMsg(message)
      isMobileApp && triggerMobileError(message)
    },
  })

  const [selectedItems, setSelectedItems] = useState(new Set<string>())
  const unselectedItems = useRef(new Set<string>())
  const selectItem = useCallback(
    (id: string) => {
      if (selectedItems.has(id)) {
        selectedItems.delete(id)
        unselectedItems.current.add(id)
      } else {
        selectedItems.add(id)
        unselectedItems.current.delete(id)
      }
      setSelectedItems(new Set(Array.from(selectedItems)))
    },
    [selectedItems],
  )

  useEffect(() => {
    const selectedItems = new Set<string>()
    filteredTripSummary?.entries.forEach((s) => {
      match(s)
        .with({ type: 'ITEM', item: P.select() }, (item) =>
          selectedItems.add(item.id),
        )
        .otherwise(() => null)
    })
    setSelectedItems(selectedItems)
  }, [filteredTripSummary?.entries])

  const selectAll = useCallback(() => {
    filteredTripSummary?.entries.forEach((s) => {
      match(s)
        .with({ type: 'ITEM', item: P.select() }, (item) =>
          selectedItems.add(item.id),
        )
        .otherwise(() => null)
    })
    unselectedItems.current.clear()
    setSelectedItems(new Set(Array.from(selectedItems)))
  }, [filteredTripSummary?.entries, selectedItems])

  useEffect(() => {
    if (filters.length > 0) {
      const filteredSet = new Set<string>()
      filteredTripSummary?.entries.forEach((s) => {
        match(s)
          .with({ type: 'ITEM', item: P.select() }, (item) => {
            if (!unselectedItems.current.has(item.id)) {
              filteredSet.add(item.id)
            }
          })
          .otherwise(() => false)
      })
      setSelectedItems(filteredSet)
    } else {
      unselectedItems.current.clear()
    }
  }, [filteredTripSummary?.entries, filters])

  const unselectAll = useCallback(() => setSelectedItems(new Set<string>()), [])

  const { closeModal, goBack } = useModalCallbacks()

  const onSubmit = useCallback(() => {
    if (!isLoading) {
      copyTemplate({
        name,
        templateId: template.id,
        regionCode: currentRegionCode,
        tripItemIds: Array.from(selectedItems),
      })
    }
  }, [
    copyTemplate,
    currentRegionCode,
    isLoading,
    name,
    selectedItems,
    template.id,
  ])

  return (
    <>
      <ModalHeader
        title={`Save ${pluralizeToString('item', selectedItems.size)} to trip`}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          {isError && (
            <BodyText variant="medium" colour="critical">
              {errorMsg}
            </BodyText>
          )}
          <Filters>
            {filteredTripSummary && (
              <TripPlannerFilters
                selectedFilters={filters}
                availableSubFilters={availableSubFilters}
                itemCount={itemCount}
              />
            )}
          </Filters>

          {filters.length > 0 &&
            filteredTripSummary?.entries.map((s) => {
              return match(s)
                .with({ type: 'ITEM', item: P.select() }, (item) => (
                  <CuratedItemSelection
                    key={item.id}
                    item={item}
                    onSelect={() => selectItem(item.id)}
                    isSelected={selectedItems.has(item.id)}
                  />
                ))
                .otherwise(() => null)
            })}

          {filters.length == 0 &&
            summarySections.map((section: CuratedTripSection, i) => (
              <CuratedSectionSelection
                key={i}
                section={section}
                selectedItems={selectedItems}
                onSelect={selectItem}
              />
            ))}
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <ButtonGroup direction="horizontal" gap={16}>
          <TextButton
            size="large"
            fit="flex"
            type="submit"
            kind="secondary"
            onClick={selectedItems.size > 0 ? unselectAll : selectAll}
          >
            {selectedItems.size > 0 ? 'Unselect All' : 'Select All'}
          </TextButton>
          <TextButton
            size="large"
            fit="flex"
            type="submit"
            kind="primary"
            disabled={selectedItems.size === 0 || isError}
            onClick={onSubmit}
          >
            Save {pluralizeToString('item', selectedItems.size)}
          </TextButton>
        </ButtonGroup>
      </ModalFooter>
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}
